import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Users, QrCode, Copy, RefreshCw, Download, Share2, UserMinus, Send, Search, X, ChevronRight,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { api, formatApiError } from "../lib/api";

/**
 * Coach-side student management section.
 * Shows the personal enrollment QR/link, lists enrolled students, and lets
 * the coach bulk-invite them to any of their owned events.
 */
export default function CoachStudentsSection() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [share, setShare] = useState(null); // {token, enroll_url}
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [filter, setFilter] = useState("");

  const loadShare = useCallback(async () => {
    setShareLoading(true);
    try {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      // Pass `?origin=` so the server builds the QR against our real domain
      // (preview in preview, production in production). Avoids the "QR sends
      // students to the wrong site after deploy" bug.
      const { data } = await api.get("/coach/me/enroll-token", origin ? { params: { origin } } : undefined);
      setShare(data);
      return data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load enrollment link");
      return null;
    } finally {
      setShareLoading(false);
    }
  }, []);

  const openShare = useCallback(async () => {
    setShareOpen(true);
    if (!share) await loadShare();
  }, [share, loadShare]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.get("/coach/me/students").then((r) => r.data);
      setStudents(Array.isArray(list) ? list : []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load students");
    } finally { setLoading(false); }
    // Pre-warm the enrollment token in the background — but don't block the
    // section render or the share button if it fails.
    loadShare();
  }, [loadShare]);

  useEffect(() => { load(); }, [load]);

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.user.first_name} ${s.user.last_name} from your roster?`)) return;
    try {
      await api.delete(`/coach/me/students/${s.user.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const lower = filter.toLowerCase();
  const filtered = students.filter((s) => {
    if (!filter) return true;
    const u = s.user;
    return (
      (u.first_name || "").toLowerCase().includes(lower) ||
      (u.last_name || "").toLowerCase().includes(lower) ||
      (u.email || "").toLowerCase().includes(lower)
    );
  });

  return (
    <section className="mt-10" data-testid="coach-students-section">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><Users size={20} className="text-[#00E676]" /> My students</h2>
          <p className="text-zinc-500 text-sm mt-1">{students.length} player{students.length === 1 ? "" : "s"} enrolled</p>
        </div>
        <div className="flex gap-2">
          <button onClick={openShare} data-testid="coach-students-share"
            className="pp-btn-secondary px-4 py-2 rounded-lg text-sm flex items-center gap-2">
            <QrCode size={14} /> Share enrollment link
          </button>
          <button onClick={() => setInviteOpen(true)} disabled={students.length === 0} data-testid="coach-students-bulk-invite"
            className="pp-btn-primary px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-40">
            <Send size={14} /> Invite all to event
          </button>
        </div>
      </div>

      {students.length > 0 && (
        <div className="relative max-w-xs mb-3">
          <Search size={14} className="absolute start-3 top-3 text-zinc-500 pointer-events-none" />
          <input className="pp-input ps-9" placeholder="Filter students…" value={filter}
            onChange={(e) => setFilter(e.target.value)} data-testid="coach-students-filter" />
        </div>
      )}

      {loading ? (
        <div className="pp-card p-10 text-center text-zinc-500">Loading…</div>
      ) : students.length === 0 ? (
        <div className="pp-card p-10 text-center" data-testid="coach-students-empty">
          <Users size={28} className="mx-auto text-zinc-600 mb-3" />
          <p className="text-zinc-400 text-sm">No students yet. Share your enrollment link or QR — players who scan it get auto-attached to your roster.</p>
          <button onClick={openShare} className="pp-btn-primary mt-4 px-4 py-2 rounded-lg text-sm">
            Show enrollment QR
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="coach-students-list">
          {filtered.map((s) => (
            <div key={s.id} className="pp-card p-4 flex items-center gap-3" data-testid={`coach-student-${s.user.id}`}>
              <Avatar className="h-12 w-12">
                <AvatarImage src={s.user.avatar_url || ""} />
                <AvatarFallback className="bg-[#007AFF] text-white">{(s.user.first_name || "?").charAt(0)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm truncate">{s.user.first_name} {s.user.last_name}</div>
                <div className="text-[11px] text-zinc-500 truncate">{s.user.email}</div>
                <div className="flex items-center gap-2 mt-1 text-[10px] text-zinc-500">
                  <span className="pp-chip text-[10px]">ELO {s.user.elo}</span>
                  {s.user.is_guest && <span className="pp-chip text-[10px] text-[#FFD60A] border-[#FFD60A]/40">Guest</span>}
                </div>
              </div>
              <button onClick={() => remove(s)} title="Remove from roster" data-testid={`coach-student-remove-${s.user.id}`}
                className="p-2 text-zinc-500 hover:text-[#FF3B30]"><UserMinus size={14} /></button>
            </div>
          ))}
          {filter && filtered.length === 0 && (
            <div className="col-span-full text-center text-zinc-500 text-sm py-6">No matches for "{filter}".</div>
          )}
        </div>
      )}

      {shareOpen && <ShareEnrollmentDialog share={share} loading={shareLoading} onClose={() => setShareOpen(false)} onRotated={(s) => setShare(s)} onRetry={loadShare} />}
      {inviteOpen && <BulkInviteDialog students={students} onClose={() => setInviteOpen(false)} />}
    </section>
  );
}

function ShareEnrollmentDialog({ share, loading, onClose, onRotated, onRetry }) {
  const [busy, setBusy] = useState(false);
  // Backend returns inline base64 PNG — no third-party round-trip.
  const qrSrc = share?.qr_png_base64;
  const copy = async () => {
    if (!share?.enroll_url) return;
    try { await navigator.clipboard.writeText(share.enroll_url); toast.success("Enrollment link copied"); }
    catch { toast.error("Couldn't copy"); }
  };
  const regen = async () => {
    if (!window.confirm("Generate a new enrollment link? The previous one will stop working.")) return;
    setBusy(true);
    try {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const { data } = await api.post(
        "/coach/me/enroll-token/regenerate",
        null,
        origin ? { params: { origin } } : undefined,
      );
      onRotated?.(data);
      toast.success("New enrollment link & QR ready");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const download = () => {
    const a = document.createElement("a");
    a.href = qrSrc;
    a.download = "pick2play-coach-enroll-qr.png";
    a.target = "_blank";
    a.click();
  };
  const nativeShare = async () => {
    if (!share?.enroll_url) return;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Enroll with me on Pick2Play", url: share.enroll_url });
      } else {
        copy();
      }
    } catch (err) {
      if (err && err.name !== "AbortError") {
        // eslint-disable-next-line no-console
        console.warn("[coach-share] share failed:", err);
      }
    }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#131313] border-white/10 text-white max-w-md" data-testid="coach-share-dialog">
        <DialogHeader><DialogTitle className="font-heading">Your enrollment QR</DialogTitle></DialogHeader>
        {!share && loading && (
          <div className="py-12 text-center text-zinc-400 text-sm" data-testid="coach-share-loading">
            <div className="inline-block h-6 w-6 border-2 border-[#00E676]/40 border-t-[#00E676] rounded-full animate-spin mb-3" />
            <div>Loading enrollment link…</div>
          </div>
        )}
        {!share && !loading && (
          <div className="py-12 text-center" data-testid="coach-share-error">
            <p className="text-sm text-zinc-400 mb-3">Couldn't load your enrollment link.</p>
            <button onClick={onRetry} className="pp-btn-primary px-4 py-2 rounded-lg text-sm">Try again</button>
          </div>
        )}
        {share && (
          <>
            <p className="text-sm text-zinc-400">Share this with your players. Anyone who scans or clicks the link can enroll under you in 1 click — they'll appear in your students list automatically.</p>
            <div className="flex justify-center bg-white p-4 rounded-xl">
              <img src={qrSrc} alt="Coach enrollment QR" className="w-56 h-56" data-testid="coach-share-qr" />
            </div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider">Enrollment link</div>
            <div className="pp-card p-3 flex items-center justify-between gap-2">
              <code className="text-[11px] text-[#00E676] truncate flex-1" data-testid="coach-share-link">{share.enroll_url}</code>
              <button onClick={copy} title="Copy" data-testid="coach-share-copy" className="p-2 rounded-lg border border-white/10 text-zinc-300 hover:text-[#00E676] hover:border-[#00E676]/40"><Copy size={14} /></button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={nativeShare} data-testid="coach-share-native" className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center justify-center gap-1"><Share2 size={12} /> Share</button>
              <button onClick={download} data-testid="coach-share-download" className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center justify-center gap-1"><Download size={12} /> Save QR</button>
              <button onClick={regen} disabled={busy} data-testid="coach-share-regen" className="pp-btn-secondary px-3 py-2 rounded-lg text-xs flex items-center justify-center gap-1 text-[#FF3B30] border-[#FF3B30]/30"><RefreshCw size={12} /> Regen</button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BulkInviteDialog({ students, onClose }) {
  const [kind, setKind] = useState("lesson");
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [picked, setPicked] = useState({}); // student_id -> bool
  const [allSelected, setAllSelected] = useState(true);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/${kind}s?mine=true`)
      .then((r) => {
        const evs = (r.data || []).filter((ev) => ev.status !== "cancelled" && ev.status !== "completed");
        setEvents(evs);
        if (evs[0] && !eventId) setEventId(evs[0].id);
      })
      .catch(() => setEvents([]));
  }, [kind, eventId]);

  const targetIds = allSelected ? null : Object.keys(picked).filter((k) => picked[k]);

  const submit = async () => {
    if (!eventId) { toast.error("Pick an event first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/coach/me/students/invite", {
        kind, event_id: eventId,
        student_ids: targetIds,
        message,
      });
      const n = (data.invited || []).length;
      toast.success(`Invited ${n} student${n === 1 ? "" : "s"}`);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#131313] border-white/10 text-white max-w-lg" data-testid="coach-bulk-invite-dialog">
        <DialogHeader><DialogTitle>Invite students to event</DialogTitle></DialogHeader>

        <div className="flex gap-2 flex-wrap">
          {[
            { id: "lesson", label: "Lesson" },
            { id: "match", label: "Match" },
            { id: "tournament", label: "Tournament" },
            { id: "league", label: "League" },
          ].map((k) => (
            <button key={k.id} onClick={() => { setKind(k.id); setEventId(""); }}
              data-testid={`bulk-kind-${k.id}`}
              className={`pp-chip ${kind === k.id ? "pp-chip-active" : ""}`}>{k.label}</button>
          ))}
        </div>

        <div>
          <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Pick one of your {kind}s</div>
          <select value={eventId} onChange={(e) => setEventId(e.target.value)} data-testid="bulk-event-select" className="pp-input">
            <option value="">— select —</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>{ev.title}</option>
            ))}
          </select>
          {events.length === 0 && (
            <p className="text-[11px] text-zinc-500 mt-1">No upcoming {kind}s you own. Create one first.</p>
          )}
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm text-zinc-300">
            <input type="checkbox" checked={allSelected} onChange={(e) => setAllSelected(e.target.checked)} data-testid="bulk-all" />
            Invite all {students.length} student{students.length === 1 ? "" : "s"}
          </label>
          {!allSelected && (
            <div className="mt-2 max-h-48 overflow-auto pp-card p-2 space-y-1" data-testid="bulk-pick-list">
              {students.map((s) => (
                <label key={s.user.id} className="flex items-center gap-2 p-1.5 hover:bg-white/5 rounded text-sm">
                  <input type="checkbox" data-testid={`bulk-pick-${s.user.id}`}
                    checked={!!picked[s.user.id]}
                    onChange={(e) => setPicked((p) => ({ ...p, [s.user.id]: e.target.checked }))} />
                  <span>{s.user.first_name} {s.user.last_name}</span>
                  <span className="text-[10px] text-zinc-500 ms-auto">{s.user.email}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <textarea data-testid="bulk-message" rows={2} placeholder="Optional message" className="pp-input"
          value={message} onChange={(e) => setMessage(e.target.value)} />

        <DialogFooter>
          <button onClick={onClose} className="pp-btn-secondary px-4 py-2 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={busy || !eventId} data-testid="bulk-submit"
            className="pp-btn-primary px-4 py-2 rounded-lg">{busy ? "Inviting…" : "Send invites"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
